import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Form,
  Label,
  Row,
  Nav,
  NavItem,
  CardTitle,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";

import Button from "../../../components/Button/Button";
import Select from "../../../components/Form/Select/Select";
import Input from "../../../components/Form/Input";
import DatePicker from "../../../components/Form/DatePicker/DatePicker";
import labelPlugin from "flatpickr/dist/plugins/labelPlugin/labelPlugin";
import moment from "moment";

import {
  getCustomeCode,
  getSelectOptions,
  getUserProfile,
} from "../../../helpers/functions_helper";
import { useFormik } from "formik";

import { validationSchemaStep2 } from "../utils/validation";
import { initialValues } from "../config/initialValues";
import immigrationService from "../services/service";

import "./styles.css";
import ProfileInfo from "./ProfileInfo";
import NavItems from "./NavItems";
import { ImmigrationDataType } from "../types";
import { mapImmigrationToForm } from "../mappers/mapper";

interface LocationState {
  immigrationData?: ImmigrationDataType;
}

const FormComponent = (props: any) => {
  const { id, immigration_id, application_id } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const searchParams = new URLSearchParams(location.search);
  const from = searchParams.get("from");
  const moduleAccess = getUserProfile("immigration");

  const toastId = React.useRef(null) as any;

  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState<any>(initialValues);
  const [activeTab, setActiveTab] = useState("immigration");
  const [activeApplicationId, setActiveApplicationId] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState("Application");

  const [loadingCode, setLoadingCode] = useState(false);

  const [loadingVisaType, setLoadingVisaType] = useState(false);
  const [disabledVisaType, setDisabledVisaType] = useState(true);
  const [visaTypeOptions, setVisaTypeOptions] = useState([]);

  const [loadingApplicationCountry, setLoadingApplicationCountry] = useState(false);
  const [disabledApplicationCountry, setDisabledApplicationCountry] = useState(true);
  const [applicationCountryOption, setApplicationCountryOption] = useState([]);

  const [loadingApplicationStatus, setLoadingApplicationStatus] = useState(false);
  const [disabledApplicationStatus, setDisabledApplicationStatus] = useState(true);
  const [applicationStatusOption, setApplicationStatusOption] = useState([]);

  const [loadingNumberOfEntries, setLoadingNumberOfEntries] = useState(false);
  const [disabledNumberOfEntriesOption, setDisabledNumberOfEntriesOption] = useState(true);
  const [numberOfEntriesOption, setNumberOfEntriesOption] = useState([]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: values,
    validationSchema: validationSchemaStep2,
    onSubmit: (data, { setErrors }) => {
      handleSubmit(data);
    },
  });

  const handleSubmit = (data: any) => {
    if (id && immigration_id && application_id) {
      update(data);
    } else {
      create(data);
    }
  };

  const create = async (data: any) => {
    try {
      setLoading(true);
      toastId.current = toast(`${t("Creating")} ${t("Immigration")}...`, {
        autoClose: false,
        isLoading: true,
      });

      const res = (await immigrationService.createApplication(data)) as any;
      if (res.data.status === "success") {
        toast.update(toastId.current, {
          type: toast.TYPE.SUCCESS,
          render: res.data.message,
          autoClose: 5000,
          isLoading: false,
          delay: 500,
        });
        setLoading(false);
        // handleRedirect();
        return navigate(
          `/immigration/view-immigration/${res.data.data.id}/${res.data.data.immigration_id}?redirect=true`,
          {
            state: {
              immigrationData: res.data.data, // Pass created data here
              createdAt: new Date().getTime(), // Add timestamp to know it's fresh
            },
          },
        );
      } else {
        toast.update(toastId.current, {
          type: toast.TYPE.ERROR,
          render: res.data.message,
          autoClose: 5000,
          isLoading: false,
          delay: 500,
        });

        if (res.data.data) {
          Object.keys(res.data.data).map((key) => {
            toast(res.data.data[key][0], {
              type: toast.TYPE.ERROR,
              autoClose: 5000,
              delay: 500,
            });
          });
        }
        setLoading(false);
      }
    } catch (err: any) {
      toast.update(toastId.current, {
        type: toast.TYPE.ERROR,
        render: err?.message,
        autoClose: 5000,
        isLoading: false,
        delay: 500,
      });
      setLoading(false);
    }
  };

  const update = async (data: any) => {
    try {
      setLoading(true);
      toastId.current = toast(`${t("Updating")} ${t("Immigration")}...`, {
        autoClose: false,
        isLoading: true,
      });

      const res = (await immigrationService.updateApplication(data)) as any;
      if (res.data.status === "success") {
        toast.update(toastId.current, {
          type: toast.TYPE.SUCCESS,
          render: res.data.message,
          autoClose: 5000,
          isLoading: false,
          delay: 500,
        });
        setLoading(false);
        // handleRedirect();
        return navigate(
          `/immigration/view-immigration/${res.data.data.id}/${res.data.data.immigration_id}?redirect=true`,
          {
            state: {
              immigrationData: res.data.data, // Pass created data here
              createdAt: new Date().getTime(), // Add timestamp to know it's fresh
            },
          },
        );
      } else {
        toast.update(toastId.current, {
          type: toast.TYPE.ERROR,
          render: res.data.message,
          autoClose: 5000,
          isLoading: false,
          delay: 500,
        });

        if (res.data.data) {
          Object.keys(res.data.data).map((key) => {
            toast(res.data.data[key][0], {
              type: toast.TYPE.ERROR,
              autoClose: 5000,
              delay: 500,
            });
          });
        }
        setLoading(false);
      }
    } catch (err: any) {
      toast.update(toastId.current, {
        type: toast.TYPE.ERROR,
        render: err?.message,
        autoClose: 5000,
        isLoading: false,
        delay: 500,
      });
      setLoading(false);
    }
  };

  const handleRedirect = (isCancel = false) => {
    if (isCancel === true) {
      if (from) {
        return navigate(`${from}?redirect=true`);
      } else {
        return navigate(`/immigration/list?redirect=true`);
        // return navigate(`/immigration/update-immigration/${id}/${immigration_id}?redirect=true`);
      }
    }
  };

  const handleChange = (fieldName: string, fieldValue: string) => {
    setValues({ ...values, [fieldName]: fieldValue });
    // setAutoSave(true);
  };

  const loadProfileData = async () => {
    setLoading(true);

    const res = await immigrationService.get({ id, immigration_id });

    if (res.data.status === "success") {
      const mapped = mapImmigrationToForm(res.data.data);

      setValues((prev: any) => ({
        ...prev,
        ...mapped,
      }));
    }

    setLoading(false);
  };

  const loadApplicationData = async () => {
    setLoading(true);

    const res = await immigrationService.get({ id, immigration_id, application_id });

    if (res.data.status === "success") {
      const mapped = mapImmigrationToForm(res.data.data);

      const selectedApplication = res.data.data?.applications?.find(
        (a: any) => String(a.id) === String(application_id),
      );

      // setValues((prev: any) => ({
      //   ...prev,
      //   ...mapped,
      // }));

      setValues((prev: any) => ({
        ...mapped, // keep your mapper output

        // 🔥 override only application-specific fields
        ...(selectedApplication
          ? {
              application_id: selectedApplication.id,
              application_code: selectedApplication.application_id,

              application_visa_type_id: selectedApplication.application_visa_type_id,
              application_visa_type_value:
                selectedApplication.application_visa_type?.visa_type_value ?? "",

              application_country_id: selectedApplication.application_country_id,
              application_country_value: selectedApplication.application_country?.name ?? "",

              application_submission_date: selectedApplication.application_submission_date,

              application_status_id: selectedApplication.application_status_id,
              application_status_value:
                selectedApplication.application_status?.application_status_value ?? "",

              application_start_date: selectedApplication.application_start_date,
              application_expiry_date: selectedApplication.application_expiry_date,
              application_duration: selectedApplication.application_duration,

              number_of_entries_id: selectedApplication.number_of_entries_id,
              number_of_entries_value:
                selectedApplication.number_of_entries?.number_of_entries_value ?? "",

              issuing_authority: selectedApplication.issuing_authority,
              reference_number: selectedApplication.reference_number,
              application_center: selectedApplication.application_center,
              case_manager: selectedApplication.case_manager,
              assigned_team: selectedApplication.assigned_team,
              client_contact: selectedApplication.client_contact,
            }
          : {}),
      }));
    }

    setLoading(false);
  };

  useEffect(() => {
    if (loadingCode === true) {
      getCustomeCode("tbl_immigration_applications", "IMM").then(function (result) {
        setValues((prev: any) => ({
          ...prev,
          application_code: result,
        }));
        setLoadingCode(false);
      });
    }
  }, [loadingCode]);

  useEffect(() => {
    if (state?.immigrationData) {
      // console.log("Using passed state data");
      const mapped = mapImmigrationToForm(state.immigrationData);

      setValues((prev: any) => ({
        ...prev,
        ...mapped,
      }));
      setLoadingCode(true);
      setLoading(false);
    } else if (id && immigration_id && !application_id) {
      // console.log("No state data, loading profile data. Application ID not present: ", application_id);
      loadProfileData();
      setLoadingCode(true);
    } else if (id && immigration_id && application_id) {
      // console.log("Loading application data");
      loadApplicationData();
    }

    application_id && setActiveApplicationId(parseInt(application_id));
  }, [state, id, immigration_id, application_id]);

  useEffect(() => {
    setLoadingApplicationCountry(true);

    getSelectOptions("tbl_master_country", "name").then(function (options) {
      setApplicationCountryOption(options);
      setLoadingApplicationCountry(false);
    });
  }, []);

  useEffect(() => {
    setLoadingVisaType(true);

    getSelectOptions("tbl_master_visa_type", "visa_type_value").then(function (options) {
      setVisaTypeOptions(options);
      setLoadingVisaType(false);
    });
  }, []);

  useEffect(() => {
    setLoadingApplicationStatus(true);

    getSelectOptions("tbl_master_immigration_status", "immigration_status_value").then(
      function (options) {
        setApplicationStatusOption(options);
        setLoadingApplicationStatus(false);
      },
    );
  }, []);

  useEffect(() => {
    setLoadingNumberOfEntries(true);
    getSelectOptions("tbl_master_number_of_entries", "number_of_entries_value").then(
      function (options) {
        setNumberOfEntriesOption(options);
        setLoadingNumberOfEntries(false);
      },
    );
  }, []);

  useEffect(() => {
    if (values.application_start_date && values.application_expiry_date) {
      const startDate = moment(values.application_start_date, "YYYY-MM-DD");
      const expiryDate = moment(values.application_expiry_date, "YYYY-MM-DD");
      const duration = expiryDate.diff(startDate, "days");
      setValues((prevValues: any) => ({
        ...prevValues,
        application_duration: duration,
      }));
    }
  }, [values.application_start_date, values.application_expiry_date]);

  return (
    <React.Fragment>
      <Row className="profile-layout">
        {/* Left Sidebar - Profile Info */}
        <ProfileInfo
          data={values}
          activeApplicationId={activeApplicationId}
          setActiveApplicationId={setActiveApplicationId}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />

        <Col col="12">
          <NavItems />

          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
            autoComplete="off"
          >
            <Card className="border-top-0 border-bottom-0 rounded-0 mb-0">
              <CardHeader>
                <CardTitle>{t("Application Details")}</CardTitle>
              </CardHeader>

              <CardBody>
                <Row>
                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Input
                        id="application_code"
                        label="Application ID"
                        type="text"
                        maxLength={15}
                        onChange={(e: any) => handleChange("application_code", e.target.value)}
                        value={values.application_code}
                        touched={validation.touched.application_code}
                        error={validation.errors.application_code}
                        required
                      />
                    </div>
                  </Col>

                  <Col className="col-12"></Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="application_visa_type_id"
                        label="Visa Type"
                        options={visaTypeOptions}
                        isClearable={true}
                        isLoading={loadingVisaType}
                        isDisabled={loadingVisaType}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("application_visa_type_id", e.value);
                          } else {
                            handleChange("application_visa_type_id", "");
                          }
                        }}
                        value={
                          values.application_visa_type_id
                            ? visaTypeOptions?.find(
                                (option: any) => option.value == values.application_visa_type_id,
                              )
                            : ""
                        }
                        touched={validation.touched.application_visa_type_id}
                        error={validation.errors.application_visa_type_id}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="application_country_id"
                        label="Application Country"
                        options={applicationCountryOption}
                        isClearable={true}
                        isLoading={loadingApplicationCountry}
                        isDisabled={loadingApplicationCountry}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("application_country_id", e.value);
                          } else {
                            handleChange("application_country_id", "");
                          }
                        }}
                        value={
                          values.application_country_id
                            ? applicationCountryOption?.find(
                                (option: any) => option.value == values.application_country_id,
                              )
                            : ""
                        }
                        touched={validation.touched.application_country_id}
                        error={validation.errors.application_country_id}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <DatePicker
                        id="application_submission_date"
                        label="Submission Date"
                        options={{
                          altInput: true,
                          altFormat: "d/m/Y",
                          dateFormat: "Y-m-d",
                          allowInput: true,
                          plugins: [labelPlugin()],
                          onChange: function (selectedDates, dateStr, instance) {
                            handleChange("application_submission_date", dateStr);
                          },
                          onClose: function (selectedDates, dateStr, instance) {
                            handleChange("application_submission_date", dateStr);
                          },
                        }}
                        value={values.application_submission_date}
                        touched={validation.touched.application_submission_date}
                        error={validation.errors.application_submission_date}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="application_status_id"
                        label="Status"
                        options={applicationStatusOption}
                        isClearable={true}
                        isLoading={loadingApplicationStatus}
                        isDisabled={loadingApplicationStatus}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("application_status_id", e.value);
                          } else {
                            handleChange("application_status_id", "");
                          }
                        }}
                        value={
                          values.application_status_id
                            ? applicationStatusOption?.find(
                                (option: any) => option.value == values.application_status_id,
                              )
                            : ""
                        }
                        touched={validation.touched.application_status_id}
                        error={validation.errors.application_status_id}
                        required
                      />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="border-top-0 border-bottom-0 rounded-0 mb-0">
              <CardHeader>
                <CardTitle>{t("Validity Tracking")}</CardTitle>
              </CardHeader>

              <CardBody>
                <Row>
                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <DatePicker
                        id="application_start_date"
                        label="Start Date"
                        options={{
                          altInput: true,
                          altFormat: "d/m/Y",
                          dateFormat: "Y-m-d",
                          allowInput: true,
                          plugins: [labelPlugin()],
                          onChange: function (selectedDates, dateStr, instance) {
                            handleChange("application_start_date", dateStr);
                          },
                          onClose: function (selectedDates, dateStr, instance) {
                            handleChange("application_start_date", dateStr);
                          },
                        }}
                        value={values.application_start_date}
                        touched={validation.touched.application_start_date}
                        error={validation.errors.application_start_date}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <DatePicker
                        id="application_expiry_date"
                        label="Expiry Date"
                        options={{
                          altInput: true,
                          altFormat: "d/m/Y",
                          dateFormat: "Y-m-d",
                          allowInput: true,
                          plugins: [labelPlugin()],
                          onChange: function (selectedDates, dateStr, instance) {
                            handleChange("application_expiry_date", dateStr);
                          },
                          onClose: function (selectedDates, dateStr, instance) {
                            handleChange("application_expiry_date", dateStr);
                          },
                        }}
                        value={values.application_expiry_date}
                        touched={validation.touched.application_expiry_date}
                        error={validation.errors.application_expiry_date}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Input
                        id="application_duration"
                        label="Duration (Days)"
                        type="number"
                        disabled={true}
                        onChange={(e: any) => handleChange("application_duration", e.target.value)}
                        value={values.application_duration}
                        touched={validation.touched.application_duration}
                        error={validation.errors.application_duration}
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="number_of_entries_id"
                        label="Number of Entries (Single / Multiple)"
                        options={numberOfEntriesOption}
                        isClearable={true}
                        isLoading={loadingNumberOfEntries}
                        isDisabled={loadingNumberOfEntries}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("number_of_entries_id", e.value);
                          } else {
                            handleChange("number_of_entries_id", "");
                          }
                        }}
                        value={
                          values.number_of_entries_id
                            ? numberOfEntriesOption?.find(
                                (option: any) => option.value == values.number_of_entries_id,
                              )
                            : ""
                        }
                        touched={validation.touched.number_of_entries_id}
                        error={validation.errors.number_of_entries_id}
                        required
                      />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="border-top-0 border-bottom-0 rounded-0 mb-0">
              <CardHeader>
                <CardTitle>{t("Authorities & References")}</CardTitle>
              </CardHeader>

              <CardBody>
                <Row>
                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Input
                        id="issuing_authority"
                        label="Issuing Authority"
                        type="text"
                        maxLength={255}
                        onChange={(e: any) => handleChange("issuing_authority", e.target.value)}
                        value={values.issuing_authority}
                        touched={validation.touched.issuing_authority}
                        error={validation.errors.issuing_authority}
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Input
                        id="reference_number"
                        label="Reference Number"
                        type="text"
                        maxLength={255}
                        onChange={(e: any) => handleChange("reference_number", e.target.value)}
                        value={values.reference_number}
                        touched={validation.touched.reference_number}
                        error={validation.errors.reference_number}
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Input
                        id="application_center"
                        label="Application Center"
                        type="text"
                        maxLength={255}
                        onChange={(e: any) => handleChange("application_center", e.target.value)}
                        value={values.application_center}
                        touched={validation.touched.application_center}
                        error={validation.errors.application_center}
                      />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="border-top-0 border-bottom-0 rounded-0 mb-0">
              <CardHeader>
                <CardTitle>{t("Case Ownership")}</CardTitle>
              </CardHeader>

              <CardBody>
                <Row>
                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Input
                        id="case_manager"
                        label="Case Manager"
                        type="text"
                        maxLength={255}
                        onChange={(e: any) => handleChange("case_manager", e.target.value)}
                        value={values.case_manager}
                        touched={validation.touched.case_manager}
                        error={validation.errors.case_manager}
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Input
                        id="assigned_team"
                        label="Assigned Team"
                        type="text"
                        maxLength={255}
                        onChange={(e: any) => handleChange("assigned_team", e.target.value)}
                        value={values.assigned_team}
                        touched={validation.touched.assigned_team}
                        error={validation.errors.assigned_team}
                      />
                    </div>
                  </Col>

                  <Col xl={6} lg={6} md={6} sm={12}>
                    <div className="mb-3">
                      <Input
                        id="client_contact"
                        label="Client Contact"
                        type="text"
                        maxLength={255}
                        onChange={(e: any) => handleChange("client_contact", e.target.value)}
                        value={values.client_contact}
                        touched={validation.touched.client_contact}
                        error={validation.errors.client_contact}
                      />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="border-top-0 rounded-0 rounded-bottom mb-0">
              <CardFooter className="d-flex flex-wrap gap-2 border-top justify-content-end sticy-footer bg-gray">
                <div className="d-flex gap-2"></div>

                <div className="ms-auto d-flex gap-2 justify-content-end">
                  <Button
                    color="primary"
                    className="btn-block"
                    type="submit"
                    icon="save"
                    loading={loading}
                  >
                    {id && immigration_id && application_id ? t("Update & Next") : t("Save & Next")}
                  </Button>

                  <Button
                    color="danger"
                    className="btn-block"
                    type="reset"
                    icon="cancel"
                    loading={loading}
                    onClick={() => handleRedirect(true)}
                  >
                    {t("Cancel")}
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </Form>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default FormComponent;
